import { BackendPermission } from "./backend-permission.enum";
import { PermissionSpecification } from "../interface/type/permission-specification.type";

/**
 * @description
 *  Lista de permisos aplicables en este frontend. Estos permisos se
 *  mapean a permisos de backend, lo que permite desacoplar la relación 1
 *  a 1 con el backend.
 *
 *  Cada permiso implica una clave asociada al componente en el que
 *  aplica, y un registro por cada control en dicho componente. Para cada
 *  control se asigna una lista de permisos de backend.
 *
 *  Por convención, la clave del componente posee la ruta hacia el
 *  template del mismo asumiendo que se encuentra dentro de "src/app". En
 *  cuanto a la clave del control de UI, la misma se compone de una serie
 *  de palabras clave separadas por ":". La última palabra debe ser el
 *  nombre que toma el control en la interfaz, y que por lo tanto es
 *  visualizado por el usuario final. Si el nombre contiene espacios, el
 *  símbolo ":", o el símbolo "/", entonces se debe especificar entre
 *  comillas simples ''.
 */

export const FrontendPermission: PermissionSpecification = {

    "views/pages/collection/collection.component": {
        "button:'Cobranza Gerente Zonal'": [
            BackendPermission.CanUploadGZCollection
        ],
        "button:'Cobranza Pago Fácil'": [
            BackendPermission.CanUploadTransportCollection
        ],
        "button:'Cobranza Transportista'": [
            BackendPermission.CanUploadTransportCollection
        ],
        "input:select:'Cobranza Payment Entities'": [
            BackendPermission.CanUploadTransportCollection,
            BackendPermission.CanUploadGZCollection
        ],
        "button:'Desarmes Transportista'": [
            // Completar (actualmente usa exceptWith).
        ],
        "input:select:'Campaña Inicio'": [
            BackendPermission.CanUploadTransportCollection
        ]
    },
    "views/pages/debts/debts.component": {
        "button:Movimientos": [
            BackendPermission.CanViewMovements
        ],
        "button:Saldos": [
            BackendPermission.CanViewAllStatements
        ],
        "button:'Reporte de morosidad por campaña'": [
            BackendPermission.CanDownloadDelinquencyReportByCampaign
        ],
        "button:'Reporte de morosidad por revendedora'": [
            BackendPermission.CanDownloadDelinquencyReportByReseller
        ],
        "button:'Cargar un nuevo pago'": [
            BackendPermission.CanUploadPayment
        ]
    },
    "views/pages/orders-cart/orders-cart.component": {
        "button:'Editar pedido'": [
            BackendPermission.CanEditOrder
        ],
        "button:'Eliminar pedido'": [
            BackendPermission.CanCancelOrder
        ],
        "button:'Eliminar pedido cancelado'": [
            BackendPermission.CanDeleteOrderCanceled
        ],
        "view:'Programa de puntos cerrado'": [
            BackendPermission.PointsProgramClosed
        ]
    },
    "views/pages/order-events-control/order-events-control.component": {
        "form:": [
            BackendPermission.CanReceivedOrderEventsProducts,
            BackendPermission.CanViewOrderEventMassiveList
        ],
        "form:button:Exportar": [
            BackendPermission.CanViewOrderEventMassiveList
        ],
        "form:button:Importar": [
            BackendPermission.CanReceivedOrderEventsProducts
        ],
        "form:select:Campaña": [
            BackendPermission.CanReceivedOrderEventsProducts
        ]
    },
    "views/pages/order-events/order-events.component": {
        "button:'Importar/Exportar'": [
            BackendPermission.CanCreateOrderEventMassive,
            BackendPermission.CanViewOrderEventMassive,
            BackendPermission.CanDownloadOrderEventsByReseller,
            BackendPermission.CanDownloadOrderEventsByZone,
            BackendPermission.CanUploadOrderEventsByReseller,
            BackendPermission.CanUploadOrderEventsByZone
        ],
        "button:'Nuevo CyR y NC'": [
            BackendPermission.CanCreateOrderEvent
        ]
    },
    "views/pages/product-report/product-report.component": {
        "button:'Exportar Query Fox'": [
            BackendPermission.CanViewInvoicedFoxProducts
        ]
    },
    "views/pages/stock/stock.component": {
        "button:'Exportar materiales'": [
            BackendPermission.CanDownloadMaterials
        ],
        "button:'Importar materiales'": [
            BackendPermission.CanUploadMaterials
        ],
        "button:'Exportar solo materiales'": [
            BackendPermission.CanDownloadMaterialsOnly
        ],
        "button:'Importar solo materiales'": [
            BackendPermission.CanUploadMaterialsOnly
        ],
        "button:'Exportar proyección de stock'": [
            BackendPermission.CanDownloadStockProjection
        ],
        "button:'Exportar materiales-piqueo'": [
            BackendPermission.CanDownloadPickingMaterials
        ],
        "button:'Importar materiales-piqueo'": [
            BackendPermission.CanUploadPickingMaterials
        ],
        "button:'Exportar materiales-stock'": [
            BackendPermission.CanDownloadStockMaterials
        ],
        "button:'Exportar materiales-stock-sin-puntos'": [
            BackendPermission.CanDownloadStockMaterialsWithoutPoints
        ],
        "button:'Importar materiales-stock'": [
            BackendPermission.CanUploadStockMaterials
        ],
        "button:'Exportar Kardex-stock'": [
            BackendPermission.CanDownloadKardexStock,
            BackendPermission.CanViewStockMaterialLog
        ],
        "button:'Exportar stock real'": [
            BackendPermission.CanDownloadRealStock
        ],
        "button:'Importar stock real'": [
            BackendPermission.CanUploadRealStock
        ],
        "button:'Exportar Kardex-stock real'": [
            BackendPermission.CanDownloadRealKardexStock,
            BackendPermission.CanViewRealStockMaterialLog
        ],
        "button:'Exportar productos-materiales'": [
            BackendPermission.CanDownloadProductMaterials
        ],
        "button:'Importar productos-materiales'": [
            BackendPermission.CanUploadProductMaterials
        ],
        "button:'Exportar materiales-reemplazo'": [
            BackendPermission.CanDownloadMaterialReplacements
        ],
        "button:'Importar materiales-reemplazo'": [
            BackendPermission.CanUploadMaterialReplacements
        ]
    },
    "views/pages/user-management/users/user-edit/user-edit.component": {
        "button:Editar": [
            BackendPermission.CanEditReferrer
        ],
        "input:select:'Entorno Logístico'": [
            BackendPermission.CanEditEntornoLogistico
        ],
        "input:select:Estado": [
            BackendPermission.CanEditResellerStatus
        ]
    },
    "views/pages/user-management/users/_subs/area-assigment/area-assigment.component": {
        "input:select:CampañaInicio": [
            BackendPermission.CanEditFirstCampaign
        ]
    },
    "views/pages/user-management/users/users-list/users-list.component": {
        "input:'Buscar por Área (5 dígitos)'": [
            BackendPermission.CanFilterUserArea
        ],
        "input:'Buscar por División'": [
            BackendPermission.CanFilterUserDivision
        ],
        "input:'Buscar por Zona'": [
            BackendPermission.CanFilterUserZone
        ],
        "button:'Nueva revendedora'": [
            BackendPermission.CanCreateUser
        ],
        "button:'Bloquear usuario'": [
            BackendPermission.CanBlockUser
        ],
        "button:'Reporte Cambios Nivel'": [
            BackendPermission.CanDownloadUserLevelChanges
        ],
        "button:'Reporte Informe de Bajas'": [
            BackendPermission.CanDownloadUsersInactiveReport
        ],
        "button:'Reporte Gestion Revendedoras'": [
            BackendPermission.CanDownloadResellersReport
        ],
        "button:'Editar zona geolocalizada usuario'": [
            BackendPermission.CanEditGeolocalizateZone
        ]
    },
    "views/pages/user-management/users/_subs/user-roles/user-roles-list.component": {
        "button:'Asignar Rol'": [
            BackendPermission.CanAssignRoles
        ],
        "button:'Eliminar Rol'": [
            BackendPermission.CanDeleteAssignedRole
        ]
    },
    "views/pages/sales-ranking/sales-ranking.component": {
        "input:'Buscar por Zona'": [
            BackendPermission.CanFilterUserZone
        ]
    },
    "views/shared/dialog-payment-list/dialog-payment-list.component": {
        "button:'Anular Pago'": [
            BackendPermission.CanDeletePayment
        ]
    },
    "views/shared/dialog-credit-notes/dialog-credit-notes.component": {
        "button:'Exportar por revendedora'": [
            BackendPermission.CanDownloadOrderEventsByReseller
        ],
        "button:'Exportar por zona'": [
            BackendPermission.CanDownloadOrderEventsByZone
        ],
        "button:'Importar por revendedora'": [
            BackendPermission.CanUploadOrderEventsByReseller
        ],
        "button:'Importar por zona'": [
            BackendPermission.CanUploadOrderEventsByZone
        ]
    },
    "views/themes/gigot/header/brand/brand.component": {
        "menu:button:Configuración": [
            BackendPermission.CanViewMenuButtonConfiguration
        ],
        "menu:button:FV": [
            BackendPermission.CanViewMenuButtonSalesFront
        ],
        "menu:button:Pedidos": [
            BackendPermission.CanViewMenuButtonOrders
        ],
        "menu:button:Piqueo": [
            BackendPermission.CanViewMenuButtonPicking
        ],
        "menu:button:Seguridad": [
            BackendPermission.CanViewMenuButtonSecurity
        ],
        "menu:button:Ventas": [
            BackendPermission.CanViewMenuButtonSales
        ],
        "menu:Configuración:'Cajas Piqueo'": [
            BackendPermission.CanViewMenuConfigurationPickingBoxes
        ],
        "menu:Configuración:'Entorno Logístico'": [
            BackendPermission.CanViewMenuConfigurationLogisticEnvironment
        ],
        "menu:Configuración:Branding": [
            BackendPermission.CanViewMenuConfigurationBranding
        ],
        "menu:Configuración:Deuda": [
            BackendPermission.CanViewMenuConfigurationDebt
        ],
        "menu:Configuración:'Guapa y Folleto Extra'": [
            BackendPermission.CanViewMenuConfigurationGuapaAndExtraBrochure
        ],
        "menu:Configuración:Transportadoras": [
            BackendPermission.CanViewMenuConfigurationTransporters
        ],
        "menu:Configuración:Mapa": [
            BackendPermission.CanViewMenuConfigurationMap
        ],
        "menu:FV:'Control de CyR'": [
            BackendPermission.CanViewMenuSalesFrontChangesAndClaimsControl
        ],
        "menu:FV:'Control de CyR y NC'": [
            BackendPermission.CanViewMenuSalesFrontChangesAndClaimsControl
        ],
        "menu:FV:'Recepción de CyR y NC'": [
            BackendPermission.CanViewMenuSalesFrontReturnsChangesAndClaimsAndCreditNotesControl
        ],
        "menu:FV:'CyR y NC'": [
            BackendPermission.CanViewMenuSalesFrontChangesClaimsAndCreditNotes
        ],
        "menu:FV:Comunicándonos": [
            BackendPermission.CanViewMenuSalesFrontCommunications
        ],
        "menu:FV:'Intencion de Reventa'": [
            BackendPermission.CanViewMenuSalesFrontCRMResellers
        ],
        "menu:FV:'Intencion de Compra'": [
            BackendPermission.CanViewMenuSalesFrontBuyIntention
        ],
        "menu:FV:Indicadores": [
            BackendPermission.CanViewMenuSalesFrontKPI
        ],
        "menu:FV:Novedades": [
            BackendPermission.CanViewMenuSalesFrontNews
        ],
        "menu:FV:Carrusel Web": [
            BackendPermission.CanViewMenuSalesFrontWebAdvertising
        ],
        "menu:FV:Puntos": [
            BackendPermission.CanViewMenuPoints
        ],
        "menu:FV:Revendedoras": [
            BackendPermission.CanViewMenuSalesFrontResellers
        ],
        "menu:Pedidos:'Alta de Transporte'": [
            BackendPermission.CanViewMenuOrdersTransportRegistration
        ],
        "menu:Pedidos:'Listado de Pedidos'": [
            BackendPermission.CanViewMenuOrdersOrdersList
        ],
        "menu:Pedidos:'Pedidos y Deudas'": [
            BackendPermission.CanViewMenuOrdersOrdersAndDebts
        ],
        "menu:Pedidos:Carrito": [
            BackendPermission.CanViewMenuOrdersShoppingCart
        ],
        "menu:Pedidos:Cobranza": [
            BackendPermission.CanViewMenuOrdersCollection
        ],
        "menu:Pedidos:Facturación": [
            BackendPermission.CanViewMenuOrdersInvoicing
        ],
        "menu:Pedidos:Premios": [
            BackendPermission.CanViewMenuOrdersPrizes
        ],
        "menu:Piqueo:'Escaneo de Cajas'": [
            BackendPermission.CanViewMenuPickingBoxScan
        ],
        "menu:Piqueo:'Liquidación 1 Transporte'": [
            BackendPermission.CanViewMenuPickingTransportSettlement1
        ],
        "menu:Piqueo:'Materiales Fuera de Piqueo'": [
            BackendPermission.CanViewMenuPickingMaterialsOutOfPicking
        ],
        "menu:Piqueo:'Ubicación de Materiales con Stock'": [
            BackendPermission.CanViewMenuPickingMaterialsWithStockLocation
        ],
        "menu:Piqueo:Transporte": [
            BackendPermission.CanViewMenuPickingTransport
        ],
        "menu:Seguridad:Roles": [
            BackendPermission.CanViewMenuSecurityRoles
        ],
        "menu:Ventas:'Estructura Comercial'": [
            BackendPermission.CanViewMenuSalesCommercialStructure
        ],
        "menu:Ventas:'Estructuras de productos'": [
            BackendPermission.CanViewMenuSalesProductStructure
        ],
        "menu:Ventas:'Niveles de Líderes'": [
            BackendPermission.CanViewMenuSalesLeaderLevels
        ],
        "menu:Ventas:'Oferta de Productos'": [
            BackendPermission.CanViewMenuSalesProductOffer
        ],
        "menu:Ventas:'Proyección de stock (captación)'": [
            BackendPermission.CanViewMenuSalesCatchmentStockProjection
        ],
        "menu:Ventas:'Proyección de stock (captación) sin Puntos'": [
            BackendPermission.CanViewMenuSalesCatchmentWithoutPointsStockProjection
        ],
        "menu:Ventas:'Proyección de stock (facturación)'": [
            BackendPermission.CanViewMenuSalesInvoicingStockProjection
        ],
        "menu:Ventas:'Reporte Cortados'": [
            BackendPermission.CanViewMenuSalesNoStockReports
        ],
        "menu:Ventas:'Stock de Materiales'": [
            BackendPermission.CanViewMenuSalesMaterialsStock
        ],
        "menu:Ventas:Alcances": [
            BackendPermission.CanViewMenuSalesScopes
        ],
        "menu:Ventas:Campañas": [
            BackendPermission.CanViewMenuSalesCampaigns
        ],
        "menu:Ventas:Comisiones": [
            BackendPermission.CanViewMenuSalesCommissions
        ],
        "menu:Ventas:Productos": [
            BackendPermission.CanViewMenuSalesProducts
        ],
        "menu:Ventas:Promociones": [
            BackendPermission.CanViewMenuSalesPromotions
        ],
        "menu:Ventas:Ranking": [
            BackendPermission.CanViewMenuSalesRanking
        ],
        "menu:Ventas:Reportes": [
            BackendPermission.CanViewMenuSalesReports
        ]
    },
    "views/themes/gigot/header/header-mobile/header-mobile.component": {
    },
    "views/pages/campaign-products/campaign-products.component": {
        "button:'Exportar Productos'": [
            BackendPermission.CanDownloadProducts
        ],
        "button:'Importar Productos'": [
            BackendPermission.CanUploadProducts
        ],
        "button:'Exportar Reemplazos y Sugeridos'": [
            BackendPermission.CanDownloadSuggestedAndReplacements
        ],
        "button:'Importar Reemplazos y Sugeridos'": [
            BackendPermission.CanUploadSuggestedAndReplacements
        ],
        "button:'Editar Oferta'": [
            BackendPermission.CanEditCampaignProducts
        ],
        "button:'Eliminar Oferta'": [
            BackendPermission.CanDeleteCampaignProducts
        ]
    },
    "views/pages/commissions/commissions.component": {
        "button:Editar": [
            BackendPermission.CanEditCommission
        ]
    },
    "views/pages/scanner/scanner.component": {
        "button:'Escaneo Masivo'": [
            BackendPermission.CanScanAllBoxes
        ],
        "button:'Ver Boton Cerrar'": [
            BackendPermission.CanViewCloseButton
        ]
    },
    // "views/pages/order-events-basket/order-events-basket-list/order-events-basket-list.component": {
    //     "button:'Editar recepción'": [
    //         BackendPermission.CanCanEditOrderEventBasket
    //     ],
    //     "input:'Buscar por Zona'": [
    //         BackendPermission.CanFilterUserZone
    //     ],
    // },
    "views/pages/order-events-returns-reports-edit/order-events-returns-reports-edit.component": {
        "button:'Reabrir caja'": [
            BackendPermission.CanReopenOrderEventBasket
        ],
        "button:'Guardar cambios'": [
            BackendPermission.CanUpdateOrderEventBasket
        ],
        "button:'Cerrar caja'": [
            BackendPermission.CanUpdateOrderEventBasket
        ],
        "button:'Exportar planilla recepcion'": [
            BackendPermission.CanDownloadOrderEventBasketReceptionSheet
        ],
        "button:'Exportar balance'": [
            BackendPermission.CanDownloadOrderEventBasketBalance
        ],
        "button:'Exportar resumen'": [
            BackendPermission.CanDownloadOrderEventBasketResume
        ]
    },
    "views/pages/news/news-list/news-list.component": {
        "button:'Nueva Novedad'": [
            BackendPermission.CanCreateCommunication
        ],
        "button:'Eliminar Novedad'": [
            BackendPermission.CanDeleteCommunication
        ],
        "button:'Editar Novedad'": [
            BackendPermission.CanEditCommunication
        ]
    },
    "views/pages/web-advertising/web-advertising-list/web-advertising-list.component": {
        "button:'Nuevo Carrusel Web'": [
            BackendPermission.CanCreateCommunication
        ],
        "button:'Eliminar Carrusel Web'": [
            BackendPermission.CanDeleteCommunication
        ],
        "button:'Editar Carrusel Web'": [
            BackendPermission.CanEditCommunication
        ]
    },
    "views/pages/news/news-edit/news-edit.component": {
        "button:'Editar Novedad'": [
            BackendPermission.CanEditCommunication
        ]
    },
    "views/pages/web-advertising/web-advertising-edit/web-advertising-edit.component": {
        "button:'Editar Carrusel Web'": [
            BackendPermission.CanEditCommunication
        ]
    },
    "views/pages/campaign/campaign.component": {
        "button:'Nueva Campaña'": [
            BackendPermission.CanCreateCampaign
        ]
    },
    "views/shared/dialog-campaign/dialog-campaign.component": {
        "button:'Editar Campaña'": [
            BackendPermission.CanEditCampaign
        ]
    },
    "views/pages/campaign/campaign-zone/campaign-zone.component": {
        "button:'Editar Campaña'": [
            BackendPermission.CanEditCampaign
        ]
    },
    "views/pages/products/products.component": {
        "button:'Nuevo Producto'": [
            BackendPermission.CanCreateProduct
        ],
        "button:'Importar Producto'": [
            BackendPermission.CanUploadProducts
        ],
        "button:'Exportar Producto'": [
            BackendPermission.CanDownloadProducts
        ],
        "button:'Editar Producto'": [
            BackendPermission.CanEditProduct
        ],
        "button:'Eliminar Producto'": [
            BackendPermission.CanDeleteProduct
        ]
    },
    "views/pages/promotions/promotions-list/promotion-list.component": {
        "button:'Nueva Promocion'": [
            BackendPermission.CanCreatePromotion
        ],
        "button:'Editar Promocion'": [
            BackendPermission.CanEditPromotion
        ],
        "button:'Eliminar Promocion'": [
            BackendPermission.CanDeletePromotion
        ]
    },
    "views/pages/scopes/scopes-list/scopes-list.component": {
        "button:'Nuevo Alcance'": [
            BackendPermission.CanCreateScope
        ],
        "button:'Editar Alcance'": [
            BackendPermission.CanEditScope
        ],
        "button:'Eliminar Alcance'": [
            BackendPermission.CanDeleteScope
        ]
    },
    "views/pages/product/product-groups-list/product-groups-list.component": {
        "button:'Nueva Estructura'": [
            BackendPermission.CanCreateProductGroup
        ],
        "button:'Editar Estructura'": [
            BackendPermission.CanEditProductGroup
        ],
        "button:'Eliminar Estructura'": [
            BackendPermission.CanDeleteProductGroup
        ]
    },
    "views/pages/campaign/commercial-structure/commercial-structure.component": {
        "button:'Editar Nivel Campaña'": [
            BackendPermission.CanEditCampaignLevel
        ],
        "button:'Eliminar Nivel Campaña'": [
            BackendPermission.CanDeleteCampaignLevel
        ],
        "button:Migración": [
            BackendPermission.CanMigrateCommercialStructure
        ]
    },
    "views/pages/orders/orders.component": {
        "button:'Liberar para facturacion'": [
            BackendPermission.CanRealeseOrders
        ],
        "button:'Importar detalles'": [
            BackendPermission.CanImportDetails
        ],
        "button:'Revertir desarme'": [
            BackendPermission.CanReverseDisarm
        ],
        "button:'Eliminar pedido cancelado'": [
            BackendPermission.CanDeleteOrderCanceled
        ]
    },
    "shared/dialog-campaign-product/dialog-campaign-product.component": {
        "input:'Maximo de Reorder'": [
            BackendPermission.CanEditReOrderQuantity
        ],
        "button:'Limpiar Reorder'": [
            BackendPermission.CanEditReOrderQuantity
        ]
    },
    "views/pages/config-map/config-map.component.ts": {
        "button:'Subir Zonas'": [
            BackendPermission.CanUploadZones
        ]
    },
    "views/pages/action-points/action-points.component.ts": {
        "button:'Historial Revendedoras'": [
            BackendPermission.CanViewPointsResellerHistory
        ],
        "button:'Administrar inventario'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Configuracion'": [
            BackendPermission.CanEditPoints
        ],

        "button:'Copiar configuracion'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Eliminar configuracion'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Ver configuracion'": [
            BackendPermission.CanViewPoints
        ],
        "button:'Ver catalogo'": [
            BackendPermission.CanViewPoints
        ],
        "button:'Carga masiva'": [
            BackendPermission.CanUploadMassiveDataLoad
        ]
    },
    "views/pages/action-points/action-points-configuration/sale-configuration/sale-configuration.component.ts": {
        "button:'Crear programa'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Editar'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Eliminar'": [
            BackendPermission.CanEditPoints
        ]
    },
    "views/pages/action-points/action-points-configuration/indication-configuration/indication-configuration.component.ts": {
        "button:'Crear programa'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Editar'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Eliminar'": [
            BackendPermission.CanEditPoints
        ]
    },
    "views/pages/action-points/action-points-configuration/continuity-configuration/continuity-configuration.component.ts": {
        "button:'Crear programa'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Editar'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Eliminar'": [
            BackendPermission.CanEditPoints
        ]
    },
    "views/pages/action-points/action-points-configuration/fidelity-configuration/fidelity-configuration.component.ts": {
        "button:'Crear programa'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Editar'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Eliminar'": [
            BackendPermission.CanEditPoints
        ]
    },
    "views/pages/action-points/action-points-configuration/advance-sale-configuration/advance-sale-configuration.component.ts": {
        "button:'Crear programa'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Editar'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Eliminar'": [
            BackendPermission.CanEditPoints
        ]
    },
    "views/pages/action-points/club-gigot-catalog/club-gigot-catalog.component.ts": {
        "button:'Agregar nuevo producto'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Editar'": [
            BackendPermission.CanEditPoints
        ]
    },
    "views/pages/action-points/stock-products/category-products/category-products.component.ts": {
        "button:'Crear nueva categoria'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Editar'": [
            BackendPermission.CanEditPoints
        ],
        "button:'Eliminar'": [
            BackendPermission.CanEditPoints
        ]
    },
    "views/pages/invoicing/invoicing-job-list/invoicing-job-list.component.ts": {
        "button:'Ver Boton Reimprimir'": [
            BackendPermission.CanGenerateInvoicingPrintFiles
        ]
    }
}
